// Racinefit - Gestionnaire Base de Données Hors Ligne Optimisé
import Dexie from 'dexie';
import { useState, useEffect } from 'react';
import { attemptSilentRelogin } from './offlineAuth';
import { secureStorage } from './secureStorage';
import { BACKEND_URL } from './backendUrl';

// Configuration de la base de données IndexedDB - Version corrigée
class RacinefitOfflineDB extends Dexie {
  constructor() {
    super('RacinefitOfflineDB');
    
    // CORRECTION MAJEURE : Résolution conflit version 1 vs 10 + Ajout dashboard_stats
    // Schéma de la base de données avec version incrémentée
    this.version(13).stores({
      members: '++id, first_name, last_name, email, phone, date_joined, status',
      subscriptions: '++id, member_id, plan_name, price, start_date, end_date, payment_status, status',
      payments: '++id, subscription_id, amount, payment_date, status, method, notes, is_installment, installment_number',
      checkins: '++id, member_id, check_in_time, check_out_time, duration_minutes',
      staff: '++id, username, full_name, role',
      dashboard_stats: '++id, active_members, active_subscriptions, today_entries, monthly_revenue, timestamp',
      syncQueue: '++id, method, url, body, timestamp, attempts, status, error',
      appSettings: '++key, value, timestamp'
    });
    
    // CORRECTION CRITIQUE: Supprimer les event handlers qui causent l'erreur 'subscribe'
    // Les événements Dexie seront gérés différemment
    
    // Configuration explicite des tables pour éviter les erreurs subscribe
    this.members = this.table('members');
    this.subscriptions = this.table('subscriptions');
    this.payments = this.table('payments');
    this.checkins = this.table('checkins');
    this.staff = this.table('staff');
    this.syncQueue = this.table('syncQueue');
    this.appSettings = this.table('appSettings');
  }
  
  // Méthode pour forcer la réinitialisation si nécessaire
  async resetDatabase() {
    console.log('🔄 Réinitialisation forcée de la base de données');
    try {
      await this.delete();
      await this.open();
      console.log('✅ Base de données réinitialisée avec succès');
    } catch (error) {
      console.error('❌ Erreur réinitialisation:', error);
      throw error;
    }
  }
}

// Instance globale de la base de données avec gestion d'erreurs améliorée
let offlineDB;

async function initializeOfflineDB() {
  try {
    offlineDB = new RacinefitOfflineDB();
    
    // Forcer l'ouverture de la base de données
    await offlineDB.open();
    
    console.log('✅ Base de données IndexedDB initialisée - Version:', offlineDB.verno);
    return offlineDB;
  } catch (error) {
    console.error('❌ Erreur initialisation IndexedDB:', error);
    
    // Tentative de récupération plus agressive
    try {
      console.log('🔄 Tentative de récupération - Suppression complète de la DB');
      
      // Fermer toutes les connexions existantes
      if (offlineDB) {
        offlineDB.close();
      }
      
      // Supprimer complètement la base
      await Dexie.delete('RacinefitOfflineDB');
      
      // Attendre un peu pour éviter les conflits
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Recréer la base de données
      offlineDB = new RacinefitOfflineDB();
      await offlineDB.open();
      
      console.log('✅ Base de données IndexedDB récupérée avec succès');
      return offlineDB;
    } catch (recoveryError) {
      console.error('❌ Échec récupération IndexedDB:', recoveryError);
      
      // Dernier fallback : localStorage uniquement
      console.log('⚠️ Fallback vers localStorage pour stockage hors ligne');
      return null;
    }
  }
}

// Initialiser la DB au chargement avec retry
export const dbPromise = initializeOfflineDB();

// Gestionnaire de synchronisation optimisé
export class OfflineManager {
  constructor() {
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    this.setupEventListeners();
    this.db = null;
    this.initPromise = this.initialize();
    
    // Indicateur de mode hors ligne
    this.offlineModeCallbacks = new Set();
    this.statsChangeCallbacks = new Set(); // CORRECTION : Callbacks pour mise à jour stats
    this.syncCompleteCallbacks = new Set(); // Callbacks après synchronisation (toast)
  }
  
  // Initialisation asynchrone de la base de données
  async initialize() {
    try {
      this.db = await dbPromise;
      if (this.db) {
        console.log('✅ OfflineManager initialisé avec IndexedDB');
      } else {
        console.log('⚠️ OfflineManager en mode localStorage fallback');
      }
    } catch (error) {
      console.error('❌ Erreur initialisation OfflineManager:', error);
    }
  }
  
  // Attendre que l'initialisation soit terminée
  async ensureInitialized() {
    await this.initPromise;
  }
  
  // Configuration des écouteurs d'événements réseau
  setupEventListeners() {
    window.addEventListener('online', () => {
      console.log('🌐 Connexion internet rétablie');
      this.isOnline = true;
      this.triggerSync();
      this.notifyOfflineMode(false);
    });
    
    window.addEventListener('offline', () => {
      console.log('🔌 Connexion internet perdue - Mode hors ligne activé');
      this.isOnline = false;
      this.notifyOfflineMode(true);
    });
  }
  
  // Abonnement aux changements de mode hors ligne
  onOfflineModeChange(callback) {
    this.offlineModeCallbacks.add(callback);
    return () => this.offlineModeCallbacks.delete(callback);
  }
  
  // Notification des changements de mode
  notifyOfflineMode(isOffline) {
    this.offlineModeCallbacks.forEach(callback => callback(isOffline));
  }

  // CORRECTION : Notification des changements de stats
  notifyStatsChanged() {
    this.statsChangeCallbacks.forEach(callback => callback());
  }

  // CORRECTION : S'abonner aux changements de stats
  onStatsChange(callback) {
    this.statsChangeCallbacks.add(callback);
    return () => this.statsChangeCallbacks.delete(callback);
  }
  
  // S'abonner à la fin d'une synchronisation (pour afficher un toast récapitulatif)
  onSyncComplete(callback) {
    this.syncCompleteCallbacks.add(callback);
    return () => this.syncCompleteCallbacks.delete(callback);
  }
  
  notifySyncComplete(result) {
    this.syncCompleteCallbacks.forEach(callback => callback(result));
  }
  
  // === MÉTHODES DE SYNCHRONISATION ===
  
  // Déclencher la synchronisation
  async triggerSync() {
    if (this.syncInProgress || !this.isOnline) return;
    
    this.syncInProgress = true;
    console.log('🔄 Début de la synchronisation...');
    
    try {
      let result = await this.syncQueuedRequests();
      // Reconnexion silencieuse si le jeton a expiré (coupure > durée de vie du jeton),
      // puis nouvelle tentative de synchronisation avec un jeton frais.
      if (result && result.needsReauth) {
        const ok = await attemptSilentRelogin();
        if (ok) {
          console.log('🔁 Nouvelle synchronisation après reconnexion silencieuse');
          result = await this.syncQueuedRequests();
        }
      }
      await this.downloadFreshData();
      console.log('✅ Synchronisation terminée avec succès');
      
      // CORRECTION CRITIQUE : Déclencher mise à jour immédiate des stats
      this.notifyStatsChanged();
      
      // Notifier le résultat (toast récapitulatif) seulement s'il y a eu de l'activité
      if (result && (result.synced > 0 || result.dropped > 0 || result.authErrors > 0 || result.needsReauth)) {
        this.notifySyncComplete(result);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la synchronisation:', error);
    } finally {
      this.syncInProgress = false;
    }
  }
  
  // Synchroniser les requêtes en attente - Version optimisée avec gestion auth
  async syncQueuedRequests() {
    await this.ensureInitialized();
    if (!this.db) return;
    
    // CORRECTION CRITIQUE : Vérifier le token avant sync
    const token = secureStorage.getToken();
    if (!token) {
      console.warn('⚠️ Aucun token d\'authentification - nettoyage queue de sync');
      await this.db.syncQueue.clear();
      return;
    }
    
    // Erreurs client (4xx) considérées comme définitivement invalides : on les abandonne
    const PERMANENT_CLIENT_ERRORS = [400, 404, 405, 409, 422];
    const MAX_RETRIES = 3;
    
    try {
      const queuedItems = await this.db.syncQueue.where('status').equals('pending').toArray();
      console.log(`📤 ${queuedItems.length} requêtes en attente de synchronisation`);
      
      let syncedCount = 0;
      let failedCount = 0;
      let droppedCount = 0;
      let authErrorCount = 0;
      let needsReauth = false;
      
      for (const item of queuedItems) {
        try {
          // CORRECTION CRITIQUE : Construire l'URL complète pour la synchronisation
          const fullUrl = item.url.startsWith('http') 
            ? item.url 
            : `${BACKEND_URL || ''}${item.url}`;
          const headers = JSON.parse(item.headers || '{}');
          headers['Authorization'] = `Bearer ${token}`;
          
          const response = await fetch(fullUrl, {
            method: item.method,
            headers,
            body: item.body
          });
          
          if (response.ok) {
            // Synchronisation réussie
            await this.db.syncQueue.delete(item.id);
            syncedCount++;
            console.log('✅ Synchronisé:', item.url);
            
            // Mettre à jour l'élément local si c'était une création
            if (item.method === 'POST' && item.body) {
              const data = JSON.parse(item.body);
              const responseData = await response.json();
              await this.updateLocalItemAfterSync(item.url, data, responseData);
            }
          } else if (response.status === 401 || response.status === 403) {
            // CORRECTION CRITIQUE : token expiré/invalide -> NE PAS supprimer les données.
            // On interrompt la synchro et on demande une reconnexion en ligne ; la file est conservée
            // intacte et sera renvoyée après une nouvelle connexion (aucune perte de données hors-ligne).
            console.warn(`🔐 Token expiré/invalide (${response.status}) : synchro interrompue, données conservées.`);
            needsReauth = true;
            break;
          } else if (PERMANENT_CLIENT_ERRORS.includes(response.status)) {
            // CORRECTION CRITIQUE : Requête définitivement invalide -> abandonner (dead-letter delete)
            // Évite que la file reste bloquée indéfiniment sur des requêtes malformées
            console.warn(`🗑️ Requête invalide abandonnée (HTTP ${response.status}): ${item.method} ${item.url}`);
            await this.db.syncQueue.delete(item.id);
            droppedCount++;
          } else {
            // Erreurs serveur (5xx) - réessayer jusqu'à MAX_RETRIES puis dead-letter
            const attempts = (item.attempts || 0) + 1;
            if (attempts >= MAX_RETRIES) {
              console.warn(`☠️ Max tentatives atteint (HTTP ${response.status}), abandon: ${item.url}`);
              await this.db.syncQueue.delete(item.id);
              droppedCount++;
            } else {
              await this.db.syncQueue.update(item.id, {
                attempts,
                error: `HTTP ${response.status}`,
                lastAttempt: Date.now()
              });
              failedCount++;
              console.warn(`⚠️ Échec sync (${response.status}), tentative ${attempts}/${MAX_RETRIES}: ${item.url}`);
            }
          }
        } catch (error) {
          // Erreur réseau - réessayer jusqu'à MAX_RETRIES puis dead-letter
          const attempts = (item.attempts || 0) + 1;
          if (attempts >= MAX_RETRIES) {
            console.warn(`☠️ Max tentatives atteint (réseau), abandon: ${item.url}`);
            await this.db.syncQueue.delete(item.id);
            droppedCount++;
          } else {
            await this.db.syncQueue.update(item.id, {
              attempts,
              error: error.message,
              lastAttempt: Date.now()
            });
            failedCount++;
            console.error(`❌ Erreur sync (tentative ${attempts}/${MAX_RETRIES}):`, item.url, error.message);
          }
        }
      }
      
      console.log(`📊 Sync terminé: ${syncedCount} réussis, ${failedCount} en attente de réessai, ${droppedCount} abandonnés, ${authErrorCount} auth invalides supprimés`);
      
      return { synced: syncedCount, failed: failedCount, dropped: droppedCount, authErrors: authErrorCount, needsReauth };
    } catch (error) {
      console.error('❌ Erreur synchronisation globale:', error);
      return { synced: 0, failed: 0, dropped: 0, authErrors: 0, needsReauth: false };
    }
  }
  
  // Vider complètement la file de synchronisation (réinitialisation manuelle)
  async clearSyncQueue() {
    await this.ensureInitialized();
    try {
      if (this.db) {
        const count = await this.db.syncQueue.count();
        await this.db.syncQueue.clear();
        console.log(`🧹 File de synchronisation réinitialisée (${count} éléments supprimés)`);
        this.notifyStatsChanged();
        return count;
      }
      localStorage.removeItem('offline_sync_queue');
      this.notifyStatsChanged();
      return 0;
    } catch (error) {
      console.error('❌ Erreur réinitialisation file de sync:', error);
      return 0;
    }
  }
  
  // Télécharger les données fraîches du serveur
  async downloadFreshData() {
    const token = secureStorage.getToken();
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    const endpoints = [
      { url: '/api/members', table: 'members' },
      { url: '/api/subscriptions', table: 'subscriptions' },
      { url: '/api/payments', table: 'payments' },
      { url: '/api/checkins/today', table: 'checkins' }
    ];
    
    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint.url, { headers: authHeaders });
        if (response.ok) {
          const data = await response.json();
          await this.bulkUpdateTable(endpoint.table, data);
          console.log(`📥 Synchronisé ${data.length} éléments depuis ${endpoint.url}`);
        }
      } catch (error) {
        console.log(`❌ Impossible de synchroniser ${endpoint.url}:`, error);
      }
    }
  }
  
  // Mise à jour en lot d'une table - Version corrigée
  async bulkUpdateTable(tableName, data) {
    await this.ensureInitialized();
    
    if (!this.db) {
      console.warn('⚠️ IndexedDB indisponible, utilisation localStorage pour bulkUpdate');
      const localKey = `offline_${tableName}`;
      localStorage.setItem(localKey, JSON.stringify(data));
      return;
    }
    
    try {
      const table = this.db.table(tableName);
      
      // Marquer tous les éléments existants
      await table.toCollection().modify({ _syncMarker: 'old' });
      
      // Ajouter/mettre à jour les nouveaux éléments
      for (const item of data) {
        await table.put({ ...item, _syncMarker: 'fresh', _offline_synced: true });
      }
      
      console.log(`✅ Bulk update ${tableName}: ${data.length} éléments mis à jour`);
      
      // Optionnel: supprimer les éléments qui n'existent plus sur le serveur
      // await table.where('_syncMarker').equals('old').delete();
    } catch (error) {
      console.error(`❌ Erreur bulk update ${tableName}:`, error);
      // Fallback localStorage
      const localKey = `offline_${tableName}`;
      localStorage.setItem(localKey, JSON.stringify(data));
      console.log(`💾 Fallback localStorage bulk update pour ${tableName}`);
    }
  }
  
  // === MÉTHODES CRUD HORS LIGNE ===
  
  // Sauvegarder des données pour utilisation hors ligne - Nouvelle méthode
  async saveDataForOfflineUse(endpoint, data) {
    await this.ensureInitialized();
    
    const tableName = this.getTableFromEndpoint(endpoint);
    
    if (!tableName) return;
    
    console.log(`💾 Sauvegarde données pour usage hors ligne: ${tableName}`);
    
    try {
      if (this.db) {
        if (tableName === 'dashboard_stats') {
          // Cas spécial pour les stats dashboard : UPSERT au lieu de clear+add destructif
          const existingStats = await this.db.dashboard_stats.orderBy('timestamp').last();
          if (existingStats) {
            // Mettre à jour les stats existantes
            await this.db.dashboard_stats.update(existingStats.id, {
              ...data,
              timestamp: Date.now(),
              _offline_cached: true
            });
            console.log(`✅ Stats dashboard mises à jour dans IndexedDB`);
          } else {
            // Ajouter nouvelles stats
            await this.db.dashboard_stats.add({
              ...data,
              timestamp: Date.now(),
              _offline_cached: true
            });
            console.log(`✅ Stats dashboard ajoutées dans IndexedDB`);
          }
        } else {
          // Cas normal pour les autres tables
          await this.bulkUpdateTable(tableName, Array.isArray(data) ? data : [data]);
          console.log(`✅ Données ${tableName} sauvegardées dans IndexedDB`);
        }
      } else {
        // Fallback localStorage
        const localKey = `offline_${tableName}`;
        if (tableName === 'dashboard_stats') {
          localStorage.setItem(localKey, JSON.stringify({ ...data, timestamp: Date.now() }));
        } else {
          localStorage.setItem(localKey, JSON.stringify(Array.isArray(data) ? data : [data]));
        }
        console.log(`✅ Données ${tableName} sauvegardées dans localStorage`);
      }
    } catch (error) {
      console.error(`❌ Erreur sauvegarde ${tableName}:`, error);
    }
  }

  // GET - Récupérer des données - Version corrigée pour fonctionnement hors ligne
  async getData(endpoint) {
    await this.ensureInitialized();
    
    const tableName = this.getTableFromEndpoint(endpoint);
    
    // CORRECTION CRITIQUE : Vérifier le mode hors ligne forcé, naturel ET spécificités mobiles
    const isOfflineMode = !this.isOnline || window.isForceOffline || !navigator.onLine || 
                         (typeof navigator.connection !== 'undefined' && navigator.connection.effectiveType === 'none');
    
    // En mode online uniquement, essayer le réseau d'abord AVEC TEST DE CONNECTIVITÉ
    if (!isOfflineMode) {
      try {
        // Test de connectivité avec timeout adapté aux mobiles pour détecter vraie coupure
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 secondes pour mobiles
        
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${secureStorage.getToken()}`,
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const networkData = await response.json();
          console.log(`📥 Données récupérées du réseau: ${Array.isArray(networkData) ? networkData.length : 1} éléments pour ${endpoint}`);
          
          // CORRECTION MOBILE : Sauvegarder en local pour utilisation hors ligne avec renforcement mobile
          if (tableName && this.db) {
            try {
              await this.bulkUpdateTable(tableName, Array.isArray(networkData) ? networkData : [networkData]);
              console.log(`💾 Données sauvegardées localement: ${tableName}`);
            } catch (error) {
              console.warn('⚠️ Erreur sauvegarde IndexedDB mobile, fallback localStorage:', error);
              // Fallback localStorage renforcé pour mobile
              const localKey = `offline_${tableName}`;
              try {
                localStorage.setItem(localKey, JSON.stringify(Array.isArray(networkData) ? networkData : [networkData]));
                localStorage.setItem(`${localKey}_timestamp`, Date.now().toString());
                console.log(`💾 Fallback localStorage mobile réussi: ${tableName}`);
              } catch (storageError) {
                console.error('❌ Erreur localStorage mobile aussi:', storageError);
              }
            }
          }
          return networkData;
        } else if (response.status === 403 || response.status === 401) {
          console.log('🔐 Problème d\'authentification - utilisation des données locales');
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('🔌 Timeout réseau mobile (8s) - passage en mode hors ligne');
          this.isOnline = false; // Forcer mode hors ligne sur timeout mobile
        } else {
          console.log('🔌 Erreur réseau mobile, utilisation des données locales:', error.message);
          this.isOnline = false; // Forcer mode hors ligne sur toute erreur réseau mobile
        }
      }
    } else {
      console.log('🔌 Mode hors ligne détecté - utilisation données locales uniquement');
    }
    
    // Fallback vers les données locales
    if (!tableName) {
      console.warn(`⚠️ Table non identifiée pour ${endpoint}`);
      return [];
    }
    
    try {
      if (this.db) {
        if (tableName === 'dashboard_stats') {
          // Cas spécial pour les stats dashboard : récupérer l'unique entrée
          const stats = await this.db.dashboard_stats.orderBy('timestamp').last();
          if (stats) {
            console.log(`📱 Stats dashboard récupérées localement`);
            // Retourner directement l'objet stats (pas un tableau)
            const { id, timestamp, _offline_cached, ...cleanStats } = stats;
            return cleanStats;
          } else {
            console.warn(`⚠️ Aucune stats dashboard trouvée en cache`);
            return {};
          }
        } else {
          // Cas normal pour les autres tables
          const data = await this.db.table(tableName).toArray();
          const filteredData = data.filter(item => !item._offline_deleted);
          console.log(`📱 Données locales récupérées: ${filteredData.length} éléments pour ${tableName}`);
          return filteredData;
        }
      } else {
        // Fallback localStorage si IndexedDB indisponible - Renforcé pour mobile Android
        const localKey = `offline_${tableName}`;
        try {
          if (tableName === 'dashboard_stats') {
            const localStats = localStorage.getItem(localKey);
            if (localStats) {
              const parsedStats = JSON.parse(localStats);
              console.log(`💾 Fallback localStorage mobile stats dashboard réussi`);
              return parsedStats;
            } else {
              console.warn(`⚠️ Aucune stats dashboard en localStorage mobile`);
              return {};
            }
          } else {
            const localData = localStorage.getItem(localKey);
            if (localData) {
              const parsedData = JSON.parse(localData);
              console.log(`💾 Fallback localStorage mobile: ${parsedData.length} éléments pour ${tableName}`);
              return parsedData;
            } else {
              console.warn(`⚠️ Aucune donnée en localStorage mobile pour ${tableName}`);
              return [];
            }
          }
        } catch (parseError) {
          console.error(`❌ Erreur parsing localStorage mobile ${tableName}:`, parseError);
          return tableName === 'dashboard_stats' ? {} : [];
        }
      }
    } catch (error) {
      console.error(`❌ Erreur récupération données locales ${tableName}:`, error);
      return tableName === 'dashboard_stats' ? {} : [];
    }
  }
  
  // POST - Créer des données - Version corrigée
  async postData(endpoint, data) {
    await this.ensureInitialized();
    
    const tableName = this.getTableFromEndpoint(endpoint);
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    
    // Créer localement avec un ID temporaire
    const localData = {
      ...data,
      id: tempId,
      _offline_created: true,
      _offline_timestamp: Date.now()
    };
    
    // Sauvegarder localement si possible
    if (tableName && this.db) {
      try {
        await this.db.table(tableName).put(localData);
        console.log(`💾 Abonnement sauvegardé localement: ${tempId}`);
      } catch (error) {
        console.warn('⚠️ Erreur sauvegarde locale, utilisation fallback localStorage:', error);
        // Fallback vers localStorage
        const localKey = `offline_${tableName}`;
        const existing = JSON.parse(localStorage.getItem(localKey) || '[]');
        existing.push(localData);
        localStorage.setItem(localKey, JSON.stringify(existing));
      }
    }
    
    // Si en ligne et pas en mode hors ligne forcé, essayer d'envoyer immédiatement
    if (this.isOnline && !window.isForceOffline) {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${secureStorage.getToken()}` 
          },
          body: JSON.stringify(data)
        });
        
        if (response.ok) {
          const serverData = await response.json();
          // Remplacer l'élément temporaire par l'élément du serveur
          if (tableName && this.db) {
            try {
              await this.db.table(tableName).delete(tempId);
              await this.db.table(tableName).put({ ...serverData, _offline_synced: true });
            } catch (error) {
              console.warn('⚠️ Erreur mise à jour après sync:', error);
            }
          }
          console.log('✅ Abonnement créé et synchronisé immédiatement');
          return serverData;
        } else {
          console.warn(`⚠️ Erreur serveur (${response.status}), abonnement en queue de sync`);
        }
      } catch (error) {
        console.log('⚠️ Erreur réseau, abonnement ajouté à la queue de synchronisation:', error);
      }
    } else {
      console.log('🔌 Mode hors ligne - Abonnement sauvegardé pour synchronisation ultérieure');
    }
    
    // Ajouter à la queue de synchronisation
    await this.addToSyncQueue('POST', endpoint, data);
    
    // Retourner les données locales avec succès
    return { 
      ...localData, 
      success: true,
      message: 'Abonnement créé en mode hors ligne'
    };
  }
  
  // PUT - Modifier des données - Version corrigée pour fonctionnement hors ligne
  async putData(endpoint, data) {
    await this.ensureInitialized();
    
    const tableName = this.getTableFromEndpoint(endpoint);
    
    // Mettre à jour localement
    if (tableName) {
      try {
        if (this.db) {
          await this.db.table(tableName).put({
            ...data,
            _offline_modified: true,
            _offline_timestamp: Date.now()
          });
        } else {
          // Fallback localStorage si IndexedDB indisponible
          const localKey = `offline_${tableName}`;
          const localData = JSON.parse(localStorage.getItem(localKey) || '[]');
          const existingIndex = localData.findIndex(item => item.id === data.id);
          if (existingIndex !== -1) {
            localData[existingIndex] = { ...data, _offline_modified: true, _offline_timestamp: Date.now() };
          } else {
            localData.push({ ...data, _offline_modified: true, _offline_timestamp: Date.now() });
          }
          localStorage.setItem(localKey, JSON.stringify(localData));
        }
      } catch (error) {
        console.error(`❌ Erreur sauvegarde locale ${tableName}:`, error);
      }
    }
    
    // Si en ligne et pas en mode hors ligne forcé, essayer d'envoyer immédiatement
    if (this.isOnline && !window.isForceOffline) {
      try {
        const response = await fetch(endpoint, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
        
        if (response.ok) {
          const serverData = await response.json();
          if (tableName && this.db) {
            try {
              await this.db.table(tableName).put({ ...serverData, _offline_synced: true });
            } catch (error) {
              console.warn('⚠️ Erreur sauvegarde serveur locale:', error);
            }
          }
          return serverData;
        }
      } catch (error) {
        console.log('Erreur réseau, ajout à la queue de synchronisation');
      }
    }
    
    // Ajouter à la queue de synchronisation
    await this.addToSyncQueue('PUT', endpoint, data);
    return data;
  }
  
  // DELETE - Supprimer des données - Version corrigée pour fonctionnement hors ligne
  async deleteData(endpoint, id) {
    await this.ensureInitialized();
    
    const tableName = this.getTableFromEndpoint(endpoint);
    
    // Marquer comme supprimé localement (soft delete)
    if (tableName) {
      try {
        if (this.db) {
          await this.db.table(tableName).update(id, {
            _offline_deleted: true,
            _offline_timestamp: Date.now()
          });
        } else {
          // Fallback localStorage si IndexedDB indisponible
          const localKey = `offline_${tableName}`;
          const localData = JSON.parse(localStorage.getItem(localKey) || '[]');
          const existingIndex = localData.findIndex(item => item.id === id);
          if (existingIndex !== -1) {
            localData[existingIndex] = { 
              ...localData[existingIndex], 
              _offline_deleted: true, 
              _offline_timestamp: Date.now() 
            };
            localStorage.setItem(localKey, JSON.stringify(localData));
          }
        }
      } catch (error) {
        console.error(`❌ Erreur suppression locale ${tableName}:`, error);
      }
    }
    
    // Si en ligne et pas en mode hors ligne forcé, essayer d'envoyer immédiatement
    if (this.isOnline && !window.isForceOffline) {
      try {
        const response = await fetch(`${endpoint}/${id}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          // Supprimer définitivement
          if (tableName && this.db) {
            try {
              await this.db.table(tableName).delete(id);
            } catch (error) {
              console.warn('⚠️ Erreur suppression définitive locale:', error);
            }
          }
          return { success: true };
        }
      } catch (error) {
        console.log('Erreur réseau, ajout à la queue de synchronisation');
      }
    }
    
    // Ajouter à la queue de synchronisation
    await this.addToSyncQueue('DELETE', `${endpoint}/${id}`, null);
    return { success: true };
  }
  
  // === MÉTHODES UTILITAIRES ===
  
  // Ajouter une requête à la queue de synchronisation - Version corrigée
  async addToSyncQueue(method, url, body) {
    await this.ensureInitialized();
    
    const queueItem = {
      method,
      url,
      body: body ? JSON.stringify(body) : null,
      headers: JSON.stringify({ 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${secureStorage.getToken()}`
      }),
      timestamp: Date.now(),
      attempts: 0,
      status: 'pending'
    };
    
    if (this.db) {
      try {
        await this.db.syncQueue.add(queueItem);
        console.log(`📤 Ajouté à la queue de sync: ${method} ${url}`);
      } catch (error) {
        console.warn('⚠️ Erreur ajout queue sync, fallback localStorage:', error);
        // Fallback vers localStorage
        const queueKey = 'offline_sync_queue';
        const existing = JSON.parse(localStorage.getItem(queueKey) || '[]');
        existing.push({ ...queueItem, id: `sync_${Date.now()}_${Math.random()}` });
        localStorage.setItem(queueKey, JSON.stringify(existing));
      }
    } else {
      // Utiliser localStorage si IndexedDB n'est pas disponible
      const queueKey = 'offline_sync_queue';
      const existing = JSON.parse(localStorage.getItem(queueKey) || '[]');
      existing.push({ ...queueItem, id: `sync_${Date.now()}_${Math.random()}` });
      localStorage.setItem(queueKey, JSON.stringify(existing));
      console.log('📤 Ajouté à la queue sync localStorage (fallback)');
    }
  }
  
  // Déterminer la table à partir de l'endpoint
  getTableFromEndpoint(endpoint) {
    if (endpoint.includes('members')) return 'members';
    if (endpoint.includes('subscriptions')) return 'subscriptions';
    if (endpoint.includes('payments')) return 'payments';
    if (endpoint.includes('checkins')) return 'checkins';
    if (endpoint.includes('staff')) return 'staff';
    if (endpoint.includes('stats/dashboard')) return 'dashboard_stats';
    return null;
  }
  
  // Mettre à jour un élément local après synchronisation
  async updateLocalItemAfterSync(url, originalData, serverData) {
    const tableName = this.getTableFromEndpoint(url);
    if (!tableName) return;
    
    try {
      if (this.db) {
        const table = this.db.table(tableName);
        
        // _offline_created n'est pas indexé -> utiliser filter()
        const tempItems = await table.filter(item => item._offline_created === true).toArray();
        // Trouver l'élément temporaire correspondant aux données envoyées
        const match = tempItems.find(item =>
          Object.keys(originalData || {}).every(k => item[k] === originalData[k])
        );
        if (match) {
          await table.delete(match.id);
        }
        if (serverData && serverData.id) {
          await table.put({ ...serverData, _offline_synced: true });
        }
      }
    } catch (error) {
      console.error('❌ Erreur mise à jour après sync:', error);
    }
  }
  
  // Obtenir les statistiques de synchronisation
  async getSyncStats() {
    try {
      if (this.db) {
        const pending = await this.db.syncQueue.where('status').equals('pending').count();
        // "Échoués" = éléments en attente ayant déjà échoué au moins une fois (en réessai)
        const failed = await this.db.syncQueue.where('attempts').above(0).count();
        
        return {
          pending,
          failed,
          isOnline: this.isOnline,
          lastSync: await this.getLastSyncTime()
        };
      }
    } catch (error) {
      console.error('❌ Erreur stats sync:', error);
    }
    
    return {
      pending: 0,
      failed: 0,
      isOnline: this.isOnline,
      lastSync: null
    };
  }
  
  // Obtenir le temps de dernière synchronisation
  async getLastSyncTime() {
    try {
      if (this.db) {
        const setting = await this.db.appSettings.get('lastSync');
        return setting ? new Date(setting.value) : null;
      }
    } catch (error) {
      console.error('❌ Erreur lecture lastSync:', error);
    }
    return null;
  }
  
  // Enregistrer le temps de synchronisation
  async setLastSyncTime() {
    try {
      if (this.db) {
        await this.db.appSettings.put({
          key: 'lastSync',
          value: Date.now(),
          timestamp: Date.now()
        });
      }
    } catch (error) {
      console.error('❌ Erreur sauvegarde lastSync:', error);
    }
  }
  
  // Vider les données locales (reset)
  async clearOfflineData() {
    try {
      if (this.db) {
        await this.db.transaction('rw', 
          this.db.members,
          this.db.subscriptions, 
          this.db.payments,
          this.db.checkins,
          this.db.syncQueue,
          async () => {
            await this.db.members.clear();
            await this.db.subscriptions.clear();
            await this.db.payments.clear();
            await this.db.checkins.clear();
            await this.db.syncQueue.clear();
          }
        );
      }
    } catch (error) {
      console.error('❌ Erreur nettoyage données offline:', error);
    }
  }
}

// Instance globale du gestionnaire hors ligne
export const offlineManager = new OfflineManager();

// Exposer pour debug/diagnostic (console navigateur + tests)
if (typeof window !== 'undefined') {
  window.offlineManager = offlineManager;
}

// Hook React pour l'état hors ligne
export const useOfflineStatus = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [syncStats, setSyncStats] = useState({ pending: 0, failed: 0 });
  
  useEffect(() => {
    const unsubscribe = offlineManager.onOfflineModeChange(setIsOffline);
    
    // Mettre à jour les stats immédiatement ET périodiquement
    const updateStats = async () => {
      const stats = await offlineManager.getSyncStats();
      setSyncStats(stats);
    };
    
    // CORRECTION CRITIQUE : Mise à jour immédiate sur changement
    const unsubscribeStats = offlineManager.onStatsChange(updateStats);
    
    updateStats();
    const interval = setInterval(updateStats, 10000); // Toutes les 10 secondes (fallback)
    
    return () => {
      unsubscribe();
      unsubscribeStats();
      clearInterval(interval);
    };
  }, []);
  
  return { isOffline, syncStats };
};

export default OfflineManager;